/**
 *
 * The following use cases need to be covered:
 *
 * 1. Layer already visible and
 *   a. data loaded already
 *   b. data not loaded yet (resolution dependent layers)
 *   c. data load attempted but failed
 *
 * 2. Layer not visible and
 *   a. data loaded already
 *   a. data not loaded yet but will when layer gets turned on (most layers)
 *   c. data not loaded yet and data may not load when layer gets turned on(resolution dependent layers)
 *
 * Some things to keep in mind:
 *
 * - If layer was not turned on, turn it on to start loading the data
 *
 */
import { reduxStore } from 'reduxStore'

import type OLLayerVector from 'ol/layer/Vector'

import * as PoziUtils from 'api/utils'
import type { ConfigLayerOptions } from 'config/ConfigLayer'
import { SET_LAYER_LOAD_FAILED, SET_LAYER_LOAD_IN_PROGRESS } from 'drawer/tabs/layers/actions'
import GetGeoJSON from 'drawer/tabs/layers/utils/GetGeoJSON'
import { getMap } from 'utils/MapUtils'

const loadAndActivateVectorLayer = (layerConf: ConfigLayerOptions) => new Promise((resolve, reject) => {
  const { title, titleid } = layerConf

  console.debug('loadAndActivateVectorLayer - Search - Load data for layer', titleid, layerConf)

  const olLayer = PoziUtils.getOpenLayersObjectFromTitleID(titleid) as OLLayerVector
  console.debug('loadAndActivateVectorLayer - olLayer', titleid, olLayer)

  if (!olLayer) {
    console.debug('loadAndActivateVectorLayer - Search - olLayer not found', titleid, layerConf)
    throw new Error('loadAndActivateVectorLayer - olLayer not found - ' + titleid)
  }

  const olLayerInitiallyVisible = olLayer.getVisible()

  console.debug('loadAndActivateVectorLayer - layer initially visible?', titleid, olLayerInitiallyVisible, olLayer)

  // Get GeoJSON as an object
  const geojson = GetGeoJSON(titleid, false)

  // Irrespective of whether the layer is visible, if the data is already there, just return that!
  if (geojson) {
    console.debug('loadAndActivateVectorLayer - data available', titleid, geojson)

    // Turn layer on in case it is not visible yet
    if (!olLayerInitiallyVisible) {
      console.debug('loadAndActivateVectorLayer - make layer visible', titleid)

      // Turn layer on but do not track while on info panel in order for it to not accidentally turn off
      PoziUtils.setMapLayerVisibility(titleid, true, false)
    }

    return resolve(geojson)
  }

  console.debug('loadAndActivateVectorLayer - data not available yet', titleid)

  const layerCanHaveSource = 'getSource' in olLayer

  if (!layerCanHaveSource) {
    console.warn('loadAndActivateVectorLayer - Search - layer has no source option!', titleid, layerConf)
    throw new Error('loadAndActivateVectorLayer - layerCanHaveSource not found - ' + titleid)
  }

  let olSource = olLayer.getSource()

  console.debug('loadAndActivateVectorLayer - olSource', titleid, olSource)

  const registerSourceLoadEvents = () => {
    if (!olSource) {
      console.warn('loadAndActivateVectorLayer - registerSourceLoadEvents - olSource not found', titleid, layerConf)
      return
    }

    console.debug('loadAndActivateVectorLayer - registerSourceLoadEvents - olSource registerSourceLoadEvents', titleid, olSource)

    olSource.once('featuresloaderror', (event) => {
      console.log('loadAndActivateVectorLayer - registerSourceLoadEvents - olSource featuresloaderror', titleid, event)

      reject(new Error('Error loading data from ' + title))
    })

    olSource.once('featuresloadend', (event) => {
      console.debug('loadAndActivateVectorLayer - registerSourceLoadEvents - olSource featuresloadend', titleid, event)

      const geojson = GetGeoJSON(titleid, false)

      resolve(geojson)
    })
  }

  // Force loading of features, just in case features do not get loaded automatically when layer gets switched on.
  // This can happen for scale dependent layers.
  const loadFeatures = () => {
    if (!olSource) {
      console.warn('loadAndActivateVectorLayer - Search - olSource not found', titleid, layerConf)

      return
    }

    registerSourceLoadEvents()

    // Just in case features do not get loaded automatically when
    // layer gets switched on
    const mapView = getMap().getView()
    const mapExtent = mapView.calculateExtent()
    const mapProjection = mapView.getProjection()
    const mapResolution = mapView.getResolution() as number

    if (!olSource?.loadFeatures) {
      console.log('loadAndActivateVectorLayer - Search - olSource.loadFeatures not found', titleid, olSource)

      const errorMsg = `The layer source has no 'loadFeatures' method: ${titleid}`

      return reject(new Error(errorMsg))
    }

    olSource.loadFeatures(mapExtent, mapResolution, mapProjection)
  }

  // It can happen that a layer has no source yet. It will likely be added the first time the layer
  // becomes visible in our Loader_XXXXX_ files.
  if (!olSource) {
    console.debug('loadAndActivateVectorLayer - Search - olSource not found', titleid, layerConf)

    olLayer.once('change:source', () => {
      console.debug('loadAndActivateVectorLayer - Search - olSource changed. Load data', titleid)

      olSource = olLayer.getSource()

      // console.debug('olSource', titleid, olSource?.getState(), olSource)

      loadFeatures()
    })
  } else {
    // The following assumes that the code that creates the layer sets the 'initialised' property
    // on the layer.
    const layerInitialised = olLayer.get('initialised')
    if (typeof layerInitialised !== 'undefined') {
      if (!layerInitialised) {
        console.debug('loadAndActivateVectorLayer - Search - olSource not yet initialised. Registering source events.', titleid, layerConf)

        loadFeatures()
      } else {
        console.debug('loadAndActivateVectorLayer - Search - olSource already initialised', titleid, layerConf)

        const layerState = reduxStore.getState().layers[titleid] || {}
        if (layerState.loadstatus === SET_LAYER_LOAD_FAILED) {
          console.debug('loadAndActivateVectorLayer - Search - layer failed to load. Rejecting.', titleid, layerConf)

          return reject(new Error('Error loading data from ' + title))
        }

        // In case layer is in process of loading, just load features again
        if (layerState.loadstatus === SET_LAYER_LOAD_IN_PROGRESS) {
          console.debug('loadAndActivateVectorLayer - Search - layer in progress of loading. Calling loadFeatures.', titleid, layerConf)

          loadFeatures()

          return
        }

        console.debug('loadAndActivateVectorLayer - Search - layer marked as loaded. Attempting to get geojson and return that.', titleid, layerConf)

        // In case layer is loaded: attempt to get the geojson data
        const geojson = GetGeoJSON(titleid, false)

        if (geojson) {
          return resolve(geojson)
        }

        console.debug('loadAndActivateVectorLayer - Search - layer has no geojson. Calling loadFeatures.', titleid, layerConf)

        loadFeatures()

        return
      }
    } else {
      console.debug('loadAndActivateVectorLayer - Search - olSource has no initialised property. Registering source events anyway.', titleid, layerConf)

      loadFeatures()
    }
  }

  // Turn layer on in case it is not visible yet
  if (!olLayerInitiallyVisible) {
    console.debug('loadAndActivateVectorLayer - make layer visible', titleid)

    // Turn layer on but do not track while on info panel in order for it to not acciclly turn off
    PoziUtils.setMapLayerVisibility(titleid, true, false)
  }
})

export default loadAndActivateVectorLayer
